import { motion, TargetAndTransition, Transition } from "motion/react";
import { CSSProperties, FC, PropsWithChildren, useState } from "react";

interface ExpandCollapseProps {
  expanded: boolean;
  style?: CSSProperties;
  onAnimationComplete?: () => void;
  horizontal?: boolean;
  hideOnCollapse?: boolean;
  overflowVisible?: boolean;
  layout?: boolean;
  transitionProps?: Transition;
}

const ExpandCollapse: FC<PropsWithChildren<ExpandCollapseProps>> = ({
  children,
  expanded,
  style,
  onAnimationComplete,
  horizontal,
  hideOnCollapse = true,
  overflowVisible,
  layout,
  transitionProps,
}) => {
  const [animating, setAnimating] = useState(false);
  const [localExpanded, setLocalExpanded] = useState(expanded);

  const animateProp: TargetAndTransition = {
    opacity: !hideOnCollapse || expanded ? 1 : 0,
  };
  const size = expanded ? "auto" : 0;
  if (horizontal) {
    animateProp.width = size;
  } else {
    animateProp.height = size;
  }

  const isAnimationComplete = (animationDefinition: CSSProperties) => {
    if (horizontal)
      return animationDefinition.width === (expanded ? "auto" : 0);
    else return animationDefinition.height === (expanded ? "auto" : 0);
  };

  const isAnimating = animating || expanded !== localExpanded;

  return (
    <motion.div
      layout={layout}
      animate={animateProp}
      style={{
        ...style,
        overflow:
          (expanded && !isAnimating) || overflowVisible ? "visible" : "hidden",
        pointerEvents: expanded && !isAnimating ? "inherit" : "none",
        display: !expanded && !isAnimating && hideOnCollapse ? "none" : "block",
      }}
      initial={false}
      transition={{ type: "Inertia", ...transitionProps }}
      onAnimationStart={() => setAnimating(true)}
      onAnimationComplete={(animationDefinition) => {
        if (isAnimationComplete(animationDefinition as CSSProperties)) {
          onAnimationComplete?.();
          setAnimating(false);
          setLocalExpanded(expanded);
        }
      }}
    >
      {children}
    </motion.div>
  );
};

export default ExpandCollapse;
